import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";
import SearchBar from "../SearchBar/searchBar";
import './headersearch.css';
import MobileOffcanvasNav from './MobileOffcanvasNav';
import { mainMenuItems, type MenuItem } from './navMenuItems';
import { trackMembershipClick } from '../../../utils/gtag';
import CLINIC_CONFIG from '../../../config/clinicConfig';

interface HeaderProps {
  routes: string[];
}

// ─── HoverDropdown — top-level dropdown ──────────────────────────────

interface HoverDropdownProps {
  label: string;
  to: string;
  children: React.ReactNode;
}

const HoverDropdown: React.FC<HoverDropdownProps> = ({ label, to, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <li
      className="nav-item dropdown"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative" }}
    >
      <Link className="nav-link dropdown-toggle" to={to}>
        {label}
      </Link>
      <ul
        className="dropdown-menu"
        style={{
          display: hovered ? "block" : "none",
          position: "absolute",
          left: "0",
          top: "100%",
          minWidth: "200px",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          transition: "opacity 0.3s ease",
          opacity: hovered ? 1 : 0,
        }}
      >
        {children}
      </ul>
    </li>
  );
};

// ─── HoverSubmenu — nested submenu ───────────────────────────────────

interface HoverSubmenuProps {
  label: string;
  to: string;
  children: React.ReactNode;
}

const HoverSubmenu: React.FC<HoverSubmenuProps> = ({ label, to, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <li
      className="dropdown-submenu"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative" }}
    >
      <Link className="dropdown-item dropdown-toggle" to={to}>
        {label}
      </Link>
      <ul
        className="dropdown-menu"
        style={{
          display: hovered ? "block" : "none",
          position: "absolute",
          left: "100%",
          top: "0",
          minWidth: "200px",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          transition: "opacity 0.3s ease",
          opacity: hovered ? 1 : 0,
        }}
      >
        {children}
      </ul>
    </li>
  );
};

// ─── Recursive menu item renderer ────────────────────────────────────

/** Renders a MenuItem as either a submenu (if it has children) or a leaf link */
const renderSubmenuItem = (item: MenuItem): React.ReactNode => {
  if (item.children && item.children.length > 0) {
    return (
      <HoverSubmenu key={item.to} label={item.label} to={item.to}>
        {item.children.map(renderSubmenuItem)}
      </HoverSubmenu>
    );
  }

  // Leaf item — external or internal link
  if (item.external) {
    return (
      <li key={item.to}>
        <a
          className="dropdown-item"
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={item.to === CLINIC_CONFIG.membership ? () => trackMembershipClick(item.to) : undefined}
        >
          {item.label}
        </a>
      </li>
    );
  }

  return (
    <li key={item.to}>
      <Link className="dropdown-item" to={item.to}>
        {item.label}
      </Link>
    </li>
  );
};

/** Renders a top-level MenuItem (dropdown with children, or simple nav link) */
const renderTopLevelItem = (item: MenuItem, isActive: (path: string) => boolean): React.ReactNode => {
  if (item.children && item.children.length > 0) {
    return (
      <HoverDropdown key={item.to + item.label} label={item.label} to={item.to}>
        {item.children.map(renderSubmenuItem)}
      </HoverDropdown>
    );
  }

  return (
    <li key={item.to + item.label} className="nav-item">
      <Link
        className={`nav-link${isActive(item.to) ? ' active' : ''}`}
        to={item.to}
        {...(isActive(item.to) && { 'aria-current': 'page' as const })}
      >
        {item.label}
      </Link>
    </li>
  );
};

// ─── Header Component ────────────────────────────────────────────────

const Header: React.FC<HeaderProps> = ({ routes }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path || location.pathname === path + '/';

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header>
      {/* Desktop Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-light py-3 d-none d-lg-block" aria-label="Main navigation">
        <div className="container-fluid ">
          <Link className="navbar-brand" to="/">
            <img
              src={"/logo.png"}
              alt={`${CLINIC_CONFIG.clinicName} - Family & Cosmetic Dental Care`}
              height="50"
              width="150"
            />
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#mainNav"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="mainNav">
            <ul className="navbar-nav d-flex justify-content-between align-items-center w-100">
              {/* ── Data-driven menu items ── */}
              {mainMenuItems.map(item => renderTopLevelItem(item, isActive))}

              <div className="position-relative">
                {/* Search Button (when closed) */}
                {!isOpen && (
                  <button
                    type="button"
                    className="btn d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "white",
                      color: "black",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      zIndex: 1051,
                    }}
                    onClick={() => setIsOpen(true)}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                )}
                {/* Expanding Search Box */}
                {isOpen && (
                  <div
                    className="d-flex align-items-center bg-white shadow rounded px-2 slide-in mobile-search-box"
                    style={{
                      zIndex: 999999999,
                    }}
                  >
                    <SearchBar routes={routes} />
                    <i
                      className="bi bi-search ms-2 text-black"
                      style={{ cursor: "pointer" }}
                      onClick={() => setIsOpen(false)}
                    ></i>
                  </div>
                )}
              </div>
            </ul>
          </div>
        </div>
      </nav>
      {/* Mobile Off-Canvas */}
      <MobileOffcanvasNav routes={routes} />
    </header>
  );
};

export default Header;
